import React from 'react';
import styled from 'styled-components';
import { useObserver } from 'mobx-react-lite';

import { Product } from 'types/graphql-overrides';
import { getPotencyStrings } from 'src/utils/helpers/product';
import { buttonStyles } from 'src/components/product-card-v2/card-action-button';
import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useBackInStock from 'src/hooks/use-back-in-stock';
import { OriginalProduct } from './use-buy-it-again-carousel';
import { useBuyItAgainSubstituteModal } from './buy-it-again-substitute-modal';
import { StrainBadge, PotencyText } from './buy-it-again-shared-styles';

type BuyItAgainOutOfStockCardProps = {
  originalProduct: OriginalProduct;
  substituteCandidates: Product[];
  trackerSource: string | null;
};

export function BuyItAgainOutOfStockCard({
  originalProduct,
  substituteCandidates,
  trackerSource,
}: BuyItAgainOutOfStockCardProps): JSX.Element {
  const user = useUser();
  const isLoggedIn = useObserver(() => user.isLoggedIn);
  const { dispensary } = useDispensary();
  const { open: openSubstituteModal } = useBuyItAgainSubstituteModal();

  const originalProductId = originalProduct._id ?? originalProduct.id ?? null;
  const backInStockProduct = { id: originalProductId ?? '' };
  const { backInStockSubscription, handleSubscribeProduct, handleUnsubscribeProduct } = useBackInStock(
    backInStockProduct,
    user
  );

  const imageUrl = originalProduct.Image ?? null;
  const productName = originalProduct.Name ?? '';
  const brandName = originalProduct.brandName ?? '';
  const { strainType } = originalProduct;
  // cannabinoidsV2 and totalTerpenes are not fetched for originalProduct, so TAC/TERPS
  // will always be null — only THC and CBD are available from the selected GQL fields.
  const { THC: THCString, CBD: CBDString } = getPotencyStrings(originalProduct);
  const potencyLines: string[] = [];
  if (THCString) potencyLines.push(`THC: ${THCString}`);
  if (CBDString) potencyLines.push(`CBD: ${CBDString}`);

  const isSubscribed = backInStockSubscription && isLoggedIn;

  const handleSuggestSimilar = (): void => {
    void openSubstituteModal({ originalProduct, substituteCandidates, trackerSource });
  };

  return (
    <CardShell data-testid='buy-it-again-out-of-stock-card'>
      <ImageSection>
        {imageUrl ? <ProductImage src={imageUrl} alt={productName} /> : <ImagePlaceholder />}
        <GrayOverlay />
        <OutOfStockBadge>OUT OF STOCK</OutOfStockBadge>
      </ImageSection>

      <ProductInfo>
        <ProductName>{productName}</ProductName>
        {brandName && <BrandName>{brandName}</BrandName>}
        {(Boolean(strainType) || potencyLines.length > 0) && (
          <StrainDetails>
            {strainType && <StrainBadge>{strainType}</StrainBadge>}
            {potencyLines.map((line) => (
              <PotencyText key={line}>{line}</PotencyText>
            ))}
          </StrainDetails>
        )}
      </ProductInfo>

      <Actions>
        <NotifyCheckboxWrapper $disabled={!dispensary || !originalProductId}>
          <NotifyCheckboxInput
            id={`notify-when-back-${originalProductId}`}
            type='checkbox'
            checked={isSubscribed}
            onChange={isSubscribed ? handleUnsubscribeProduct : handleSubscribeProduct}
            disabled={!dispensary || !originalProductId}
            data-testid='notify-when-back-btn'
          />
          <NotifyCheckboxLabel htmlFor={`notify-when-back-${originalProductId}`} $checked={isSubscribed}>
            Notify me when back
          </NotifyCheckboxLabel>
        </NotifyCheckboxWrapper>
        {substituteCandidates.length > 0 && (
          <SuggestButton onClick={handleSuggestSimilar} data-testid='suggest-similar-item-btn'>
            Suggest similar
          </SuggestButton>
        )}
      </Actions>
    </CardShell>
  );
}

const CardShell = styled.div`
  background: #fff;
  border: 1px solid #e3e7e9;
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  width: 184px;

  @media screen and (min-width: 1110px) {
    width: 220px;
  }
`;

const ImageSection = styled.div`
  aspect-ratio: 1 / 1;
  margin-bottom: 8px;
  position: relative;
`;

const ProductImage = styled.img`
  display: block;
  filter: grayscale(100%);
  height: 100%;
  margin: 0 auto;
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  width: 100%;
`;

const ImagePlaceholder = styled.div`
  background-color: ${({ theme }) => theme.colors.grey[90]};
  border-radius: 4px;
  height: 100%;
  width: 100%;
`;

const GrayOverlay = styled.div`
  background: rgba(255, 255, 255, 0.55);
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const OutOfStockBadge = styled.div`
  background: rgba(0, 0, 0, 0.75);
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  left: 0;
  letter-spacing: 0.04em;
  padding: 4px 6px;
  position: absolute;
  top: 0;
`;

const ProductInfo = styled.div`
  flex: 1;
  margin-bottom: 12px;
`;

const ProductName = styled.p`
  color: #121516;
  display: -webkit-box;
  font-size: 14px;
  font-weight: 600;
  line-height: ${16 / 14};
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const BrandName = styled.p`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  color: #646d72;
  display: -webkit-box;
  font-size: 12px;
  line-height: 1;
  margin-top: 4px;
  overflow: hidden;
`;

const StrainDetails = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NotifyCheckboxWrapper = styled.div<{ $disabled?: boolean }>`
  align-items: center;
  display: flex;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  position: relative;
`;

const NotifyCheckboxInput = styled.input`
  cursor: pointer;
  height: 100%;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 2;

  &:disabled {
    cursor: default;
  }

  &.focus-visible + label {
    box-shadow: 0 0 0 2pt rgba(11, 153, 230, 0.4);
  }
`;

const NotifyCheckboxLabel = styled.label<{ $checked?: boolean }>`
  /* Matches CheckboxInput in src/components/core/checkbox-input — same SVG icons and layout */
  background-image: url(${({ $checked }) =>
    $checked ? `'/icons/checked-icon-plum.svg'` : `'/icons/unchecked-icon.svg'`});
  background-position: left center;
  background-repeat: no-repeat;
  background-size: 15px;
  color: var(--dcm-filter-text-color, ${({ theme }) => theme.colors.primaryBlack});
  cursor: pointer;
  font-size: 13px;
  line-height: 1.4;
  padding: 0 0 0 22px;
  position: relative;
  z-index: auto;
`;

const SuggestButton = styled.button`
  ${buttonStyles}

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;
